"use client";

import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { AnimatePresence, motion } from "motion/react";
import React from "react";
import MotionHighlight from "@/components/effects/MotionHighlight";
import { breakpointClasses, cn, splitBreakpointProps } from "@/lib/utils";
import type { DropdownMenuContentProps } from "../types";
import { useDropdownMenu } from "./dropdown-menu-root";

const DEFAULT_TRANSITION = { duration: 0.2 };

function DropdownMenuContent({
  sideOffset = 4,
  transition = DEFAULT_TRANSITION,
  className,
  children,
  ...props
}: DropdownMenuContentProps) {
  const { isOpen, highlightTransition, animateOnHover } = useDropdownMenu();

  const { bp, native } = splitBreakpointProps(props);
  const classes = breakpointClasses(bp);

  return (
    <AnimatePresence>
      {isOpen && (
        <DropdownMenuPrimitive.Portal
          forceMount
          data-slot="dropdown-menu-portal"
        >
          <DropdownMenuPrimitive.Content
            asChild
            sideOffset={sideOffset}
            {...native}
          >
            <motion.div
              key="dropdown-menu-content"
              data-slot="dropdown-menu-content"
              className={cn(
                "z-50 max-h-[var(--radix-dropdown-menu-content-available-height)] min-w-[8rem] overflow-y-auto overflow-x-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-dropdown-menu-content-transform-origin]",
                className,
                classes,
              )}
              initial={{
                opacity: 0,
                scale: 0.95,
              }}
              animate={{
                opacity: 1,
                scale: 1,
              }}
              exit={{
                opacity: 0,
                scale: 0.95,
              }}
              transition={transition}
              style={{ willChange: "opacity, transform" }}
            >
              <MotionHighlight
                hover
                controlledItems
                className="rounded-sm bg-accent"
                transition={highlightTransition}
                enabled={animateOnHover}
              >
                {children}
              </MotionHighlight>
            </motion.div>
          </DropdownMenuPrimitive.Content>
        </DropdownMenuPrimitive.Portal>
      )}
    </AnimatePresence>
  );
}

DropdownMenuContent.displayName = "DropdownMenu.Content";

export default DropdownMenuContent;
